import type { FC, ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { useAppState } from '../../../AppStateProvider';
import { Page } from '../../../domain/page';
import { useRequestAccessMutation } from '../../../features/user/useRequestAccessMutation';
import { useUserQuery } from '../../../features/user/useUserQuery';
import { useIsSwUser } from '../../../hooks/useIsSwUser';
import { AppMode } from '../../../services/appStore';
import { AssetsService } from '../../../services/AssetsService';
import { useDispatchTrackEventWithLocation } from '../../../services/tracking/hooks/useDispatchTrackEventWithLocation';
import { usePredefinedPageViewTracking } from '../../../services/tracking/hooks/usePredefinedPageViewTracking';
import { GainAccessButton } from '../../Button';
import { CloseExtensionButton } from '../../CloseExtensionButton';
import {
  LockScreenContainer,
  StyledAccessRequestedHeading,
  StyledAccessRequestedImage,
  StyledAccessRequestedText,
  StyledCloseButtonContainer,
  StyledLockScreenHeading,
  StyledLockScreenImage,
  StyledRestrictedAccessText,
} from '../styles';

import { useHasRequestedAccess } from './useHasRequestedAccess';

const GainAccessView: FC<{ onGainAccessClick: () => void }> = (props) => {
  const { t } = useTranslation();

  return (
    <>
      <StyledLockScreenImage
        data-automation="RestrictedScreenImage"
        src={AssetsService.assetUrl('/images/extension-illustration.png')}
        alt="extension"
        variant="restricted"
      />
      <StyledLockScreenHeading data-automation="RestrictedScreenHeading">
        {t('common.sales_intelligence')}
      </StyledLockScreenHeading>
      <StyledRestrictedAccessText data-automation="RestrictedScreenText">
        {t('restricted_access.text')}
      </StyledRestrictedAccessText>
      <GainAccessButton onClick={props.onGainAccessClick} />
    </>
  );
};

const AccessRequestedView: FC = () => {
  const { t } = useTranslation();

  return (
    <>
      <StyledAccessRequestedImage src={AssetsService.assetUrl('/images/balloons.svg')} alt="balloons" />
      <StyledAccessRequestedHeading>{t('access_requested.title')}</StyledAccessRequestedHeading>
      <StyledAccessRequestedText>{t('access_requested.text')}</StyledAccessRequestedText>
    </>
  );
};

const RestrictedAccessScreen = () => {
  const isSwUser = useIsSwUser();
  const { hasRequestedAccess, setHasRequestedAccess } = useHasRequestedAccess();
  const requestAccessMutation = useRequestAccessMutation();
  const dispatchTrackEventWithLocation = useDispatchTrackEventWithLocation();
  const { mode } = useAppState();

  usePredefinedPageViewTracking({ page: Page.NoAccess, isSwUser });

  const handleClickOnGainAccess = () => {
    setHasRequestedAccess(true);
    requestAccessMutation.mutate('extension');
    dispatchTrackEventWithLocation('hooks', 'request access', 'gain access');
  };

  return (
    <LockScreenContainer variant="restricted">
      {mode !== AppMode.Salesforce && mode !== AppMode.Standalone ? (
        <StyledCloseButtonContainer>
          <CloseExtensionButton />
        </StyledCloseButtonContainer>
      ) : null}
      {hasRequestedAccess ? <AccessRequestedView /> : <GainAccessView onGainAccessClick={handleClickOnGainAccess} />}
    </LockScreenContainer>
  );
};

export const RestrictedAccessGuard = ({ children }: { children: ReactNode }) => {
  const { data } = useUserQuery();

  if (data && !data.isExtensionEnabled) {
    return <RestrictedAccessScreen />;
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};
